'use client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { walletApi } from '../api/wallet.api';

import { ONRAMPER_PAYMENTS_ENABLED } from '@/constants';

export const WALLET_QUERY_KEYS = {
  all: ['wallet'] as const,
  supportedAssets: () => [...WALLET_QUERY_KEYS.all, 'supported-assets'] as const,
  supportedCountries: () => [...WALLET_QUERY_KEYS.all, 'supported-countries'] as const,
  paymentTypes: (fiat: string, country: string, crypto: string) =>
    [...WALLET_QUERY_KEYS.all, 'payment-types', { fiat, country, crypto }] as const,
  quotes: (fiat: string, crypto: string, amount: number, country: string, pm: string) =>
    [...WALLET_QUERY_KEYS.all, 'quotes', { fiat, crypto, amount, country, pm }] as const,
};

// These hit the Onramper API. Only fetch when Onramper payments are actually
// enabled AND the deposit modal is open — otherwise they fire on every page
// load, since the modal component (and this hook) stays mounted while closed.
export const useSupportedAssets = (enabled = true) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.supportedAssets(),
    queryFn: () => walletApi.getSupportedAssets(),
    enabled: ONRAMPER_PAYMENTS_ENABLED && enabled,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const useSupportedCountries = (enabled = true) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.supportedCountries(),
    queryFn: () => walletApi.getSupportedCountries(),
    enabled: ONRAMPER_PAYMENTS_ENABLED && enabled,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const usePaymentTypes = (fiat: string, country: string, crypto: string, enabled = true) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.paymentTypes(fiat, country, crypto),
    queryFn: () => walletApi.getPaymentTypes(fiat, country, crypto),
    enabled: ONRAMPER_PAYMENTS_ENABLED && enabled && !!fiat && !!country && !!crypto,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
};

export const useQuotes = (
  fiat: string,
  crypto: string,
  amount: number,
  country: string,
  pm: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.quotes(fiat, crypto, amount, country, pm),
    queryFn: () => walletApi.getQuotes(fiat, crypto, amount, country, pm),
    enabled:
      ONRAMPER_PAYMENTS_ENABLED && enabled && !!fiat && !!crypto && !!amount && !!country && !!pm,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });
};

export const useInitiateDeposit = () => {
  return useMutation({
    mutationFn: walletApi.initiateDeposit,
  });
};

// Poll a deposit's status while the checkout iframe is open. Stops polling once
// the deposit reaches a terminal state so we don't hammer the backend.
export const useDepositStatus = (depositId: number | null, enabled = true) => {
  return useQuery({
    queryKey: [...WALLET_QUERY_KEYS.all, 'deposit-status', depositId],
    queryFn: () => walletApi.getDepositStatus(depositId as number),
    enabled: enabled && !!depositId,
    refetchInterval: (query) => {
      const status = (query.state.data as { status?: string } | undefined)?.status;
      return status === 'completed' || status === 'failed' || status === 'canceled' ? false : 5000;
    },
  });
};

export const useLinkWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: walletApi.linkWallet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.all });
    },
  });
};
